import React, { useEffect, useState } from 'react';
import {
  Box, Button, FormControl, FormLabel, Heading, Input, Stack, Text, Divider, useToast,
} from '@chakra-ui/react';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrateur',
  organizer: "Gestionnaire d'événements",
  viewer: 'Accueil',
};

const errorMessage = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d: any) => d?.msg).filter(Boolean).join(' ; ') || fallback;
  return fallback;
};

const ProfilePage: React.FC = () => {
  const { user, updateUser } = useAuth();
  const toast = useToast();

  const [firstName, setFirstName] = useState(user?.first_name ?? '');
  const [lastName, setLastName] = useState(user?.last_name ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [saving, setSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    setFirstName(user?.first_name ?? '');
    setLastName(user?.last_name ?? '');
    setPhone(user?.phone ?? '');
  }, [user?.first_name, user?.last_name, user?.phone]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const updated = await apiService.updateProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim() || null,
      });
      updateUser(updated);
      toast({ title: 'Profil mis à jour', status: 'success' });
    } catch (e: any) {
      toast({ title: 'Mise à jour impossible', description: errorMessage(e, 'Erreur'), status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const mismatch = confirmPassword !== '' && newPassword !== confirmPassword;
  const canChange = !!currentPassword && !!newPassword && newPassword === confirmPassword;

  const changePassword = async () => {
    setChanging(true);
    try {
      await apiService.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast({ title: 'Mot de passe modifié', status: 'success' });
    } catch (e: any) {
      toast({ title: 'Changement impossible', description: errorMessage(e, 'Erreur'), status: 'error' });
    } finally {
      setChanging(false);
    }
  };

  if (!user) return null;

  return (
    <Box maxW="lg" mx="auto" bg="white" p={6} borderRadius="md" shadow="sm" data-testid="profile-page">
      <Heading size="md" mb={4}>Mon profil</Heading>
      <Stack spacing={4}>
        <Box>
          <Text fontSize="sm" color="gray.600">E-mail : <span data-testid="profile-email">{user.email}</span></Text>
          <Text fontSize="sm" color="gray.600">Rôle : <span data-testid="profile-role">{ROLE_LABELS[user.role] ?? user.role}</span></Text>
        </Box>
        <FormControl>
          <FormLabel>Prénom</FormLabel>
          <Input value={firstName} onChange={(e: any) => setFirstName(e.target.value)} data-testid="profile-first-name" />
        </FormControl>
        <FormControl>
          <FormLabel>Nom</FormLabel>
          <Input value={lastName} onChange={(e: any) => setLastName(e.target.value)} data-testid="profile-last-name" />
        </FormControl>
        <FormControl>
          <FormLabel>Téléphone</FormLabel>
          <Input value={phone} onChange={(e: any) => setPhone(e.target.value)} data-testid="profile-phone" />
        </FormControl>
        <Button colorScheme="blue" onClick={saveProfile} isLoading={saving}
          isDisabled={!firstName.trim() || !lastName.trim()} data-testid="profile-save">
          Enregistrer
        </Button>

        <Divider />
        <Heading size="sm">Changer de mot de passe</Heading>
        <FormControl>
          <FormLabel>Mot de passe actuel</FormLabel>
          <Input type="password" value={currentPassword} onChange={(e: any) => setCurrentPassword(e.target.value)} data-testid="profile-current-password" />
        </FormControl>
        <FormControl isInvalid={mismatch}>
          <FormLabel>Nouveau mot de passe</FormLabel>
          <Input type="password" value={newPassword} onChange={(e: any) => setNewPassword(e.target.value)} data-testid="profile-new-password" />
        </FormControl>
        <FormControl isInvalid={mismatch}>
          <FormLabel>Confirmer le nouveau mot de passe</FormLabel>
          <Input type="password" value={confirmPassword} onChange={(e: any) => setConfirmPassword(e.target.value)} data-testid="profile-confirm-password" />
          {mismatch && <Text color="red.500" fontSize="sm" data-testid="profile-password-mismatch">Les mots de passe ne correspondent pas.</Text>}
        </FormControl>
        <Button onClick={changePassword} isLoading={changing} isDisabled={!canChange} data-testid="profile-change-password">
          Changer le mot de passe
        </Button>
      </Stack>
    </Box>
  );
};

export default ProfilePage;
